import React, {useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {deleteField, doc, serverTimestamp, updateDoc} from 'firebase/firestore'
import {ref, uploadBytesResumable, getDownloadURL} from 'firebase/storage'
import {useAuth} from '../../../../modules/auth'
import db, {storage} from '../../../../../services/firebase'
import Button from 'app/components/Button'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import Dropzone from 'react-dropzone'

interface StartReviewProps {
  isOpen: boolean
  onClose: () => void
  id: string
  systemId?: string
  accountId?: string
  remediatedNote?: {note: string; uploadedAt: any} | string | null
  remediatedFile?: {name: string; url: string; date: string; size: number} | null
  mode?: string
}

const UploadEvidenceModal: React.FC<StartReviewProps> = ({
  isOpen,
  onClose,
  id,
  systemId,
  accountId,
  remediatedNote,
  remediatedFile,
  mode,
}) => {
  const [showModal, setShowModal] = useState(false)
  const {currentUser} = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [evidenceType, setEvidenceType] = useState<'note' | 'upload'>('note')
  const [note, setNote] = useState('')
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [existingFile, setExistingFile] = useState<{
    name: string
    url: string
    date: string
    size: number
  } | null>(null)
  const [isNoteSaving, setIsNoteSaving] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  useEffect(() => {
    setShowModal(isOpen)
    if (isOpen) {
      if (remediatedNote) {
        setEvidenceType('note')
        if (typeof remediatedNote === 'object' && remediatedNote?.note) {
          setNote(remediatedNote.note)
        } else if (typeof remediatedNote === 'string') {
          setNote(remediatedNote)
        } else {
          setNote('')
        }
        setUploadedFile(null)
        setExistingFile(null)
      } else if (remediatedFile) {
        setEvidenceType('upload')
        setExistingFile(remediatedFile)
        setUploadedFile(null)
        setNote('')
      } else {
        setEvidenceType('note')
        setNote('')
        setUploadedFile(null)
        setExistingFile(null)
      }
    }
  }, [isOpen, remediatedNote, remediatedFile])

  const handleClose = () => {
    setShowModal(false)
    onClose()
  }

  const handleUpload = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setUploadedFile(acceptedFiles[0])
      setExistingFile(null)
    }
  }

  const handleRemoveFile = () => {
    setUploadedFile(null)
  }

  const handleDownloadFile = () => {
    if (existingFile?.url) {
      const link = document.createElement('a')
      link.href = existingFile.url
      link.download = existingFile.name
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const handleDeleteFile = async () => {
    const requiredFields = {
      tenantId: currentUser?.tenantId || '',
      reviewId: id,
      systemId: systemId,
      accountId: accountId,
    }

    const missingFields = Object.entries(requiredFields)
      .filter(([_, value]) => !value)
      .map(([key]) => key)

    if (missingFields.length > 0) {
      showErrorToast({message: 'Missing required fields: ' + missingFields.join(', ')})
      return
    }

    try {
      const reviewRef = doc(db as any, 'tenants', currentUser?.tenantId || '', 'reviews', id)
      await updateDoc(reviewRef, {
        [`systems.${systemId}.users.${accountId}.remediatedFile`]: deleteField(),
      })
      setExistingFile(null)
      setUploadedFile(null)
      showSuccessToast({message: 'File deleted successfully'})
      handleClose()
    } catch (error) {
      showErrorToast({message: 'Error deleting file'})
    }
  }

  const handleSaveNote = async () => {
    setIsNoteSaving(true)
    const requiredFields = {
      tenantId: currentUser?.tenantId || '',
      reviewId: id,
      systemId: systemId,
      accountId: accountId,
    }

    const missingFields = Object.entries(requiredFields)
      .filter(([_, value]) => !value)
      .map(([key]) => key)

    if (missingFields.length > 0) {
      showErrorToast({message: 'Missing required fields: ' + missingFields.join(', ')})
      setIsNoteSaving(false)
      return
    }

    try {
      const reviewRef = doc(db as any, 'tenants', currentUser?.tenantId || '', 'reviews', id)
      await updateDoc(reviewRef, {
        [`systems.${systemId}.users.${accountId}.remediatedNote`]: {
          note: note.trim() || '',
          uploadedAt: serverTimestamp(),
        },
        [`systems.${systemId}.users.${accountId}.remediatedFile`]: deleteField(),
      })
      setIsNoteSaving(false)
      showSuccessToast({message: 'Note saved successfully'})
      handleClose()
    } catch (error) {
      showErrorToast({message: 'Error saving note'})
      setIsNoteSaving(false)
    }
  }

  const handleUploadFile = async (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      showErrorToast({message: 'File size exceeds 10MB limit. Please upload a smaller file.'})
      return
    }

    setIsLoading(true)
    setUploadProgress(1)

    const fileName = file.name
    const fileRef = ref(
      storage,
      `tenants/${currentUser?.tenantId}/reviews/${id}/remediated_evidence/${accountId}/${fileName}`
    )
    const uploadTask = uploadBytesResumable(fileRef, file)

    const progressInterval = setInterval(() => {
      setUploadProgress((prevProgress) => {
        if (prevProgress >= 90) {
          clearInterval(progressInterval)
          return prevProgress
        }
        return prevProgress + 10
      })
    }, 200)

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
        setUploadProgress(progress)
      },
      (error) => {
        clearInterval(progressInterval)
        setUploadProgress(0)
        setIsLoading(false)
        showErrorToast({message: 'Error uploading file. Please try again.'})
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)

          const fileInfo = {
            name: fileName,
            url: downloadURL,
            date: new Date().toISOString(),
            size: file.size,
          }

          const reviewRef = doc(db as any, 'tenants', currentUser?.tenantId || '', 'reviews', id)
          await updateDoc(reviewRef, {
            [`systems.${systemId}.users.${accountId}.remediatedFile`]: fileInfo,
            [`systems.${systemId}.users.${accountId}.remediatedNote`]: deleteField(),
          })

          clearInterval(progressInterval)
          setUploadProgress(0)
          setIsLoading(false)
          showSuccessToast({message: 'Evidence uploaded successfully'})
          handleClose()
        } catch (error) {
          showErrorToast({message: 'Error saving file metadata'})
          setUploadProgress(0)
          setIsLoading(false)
        }
      }
    )
  }

  const handleRemoveNote = async () => {
    const requiredFields = {
      tenantId: currentUser?.tenantId || '',
      reviewId: id,
      systemId: systemId,
      accountId: accountId,
    }

    const missingFields = Object.entries(requiredFields)
      .filter(([_, value]) => !value)
      .map(([key]) => key)

    if (missingFields.length > 0) {
      showErrorToast({message: 'Missing required fields: ' + missingFields.join(', ')})
      return
    }

    try {
      const reviewRef = doc(db as any, 'tenants', currentUser?.tenantId || '', 'reviews', id)
      await updateDoc(reviewRef, {
        [`systems.${systemId}.users.${accountId}.remediatedNote`]: deleteField(),
      })
      setNote('')
      showSuccessToast({message: 'Note deleted successfully'})
      handleClose()
    } catch (error) {
      showErrorToast({message: 'Error deleting note'})
    }
  }

  const handleSubmit = async () => {
    if (evidenceType === 'note') {
      await handleSaveNote()
    } else if (uploadedFile) {
      await handleUploadFile(uploadedFile)
    } else {
      showErrorToast({message: 'No file selected for upload'})
    }
  }

  return (
    <Modal show={showModal} onHide={handleClose} backdropClassName='bg-opacity-1' centered>
      <Modal.Header style={{padding: '20px'}}>
        <Modal.Title>{mode} Evidence</Modal.Title>
        <button
          type='button'
          className='btn-close me-0.5'
          onClick={handleClose}
          aria-label='Close'
        ></button>
      </Modal.Header>
      <Modal.Body style={{padding: '20px'}}>
        <div className='fs-6 mb-4'>
          Add or upload evidence to indicate that access has been remediated.
        </div>
        {mode === 'Edit' ? (
          <></>
        ) : (
          <div className='mb-6'>
            <div className='form-check d-flex align-items-center mb-2'>
              <input
                className='form-check-input'
                type='radio'
                name='evidenceType'
                id='addNote'
                checked={evidenceType === 'note'}
                onChange={() => {
                  setEvidenceType('note')
                  setUploadedFile(null)
                }}
                style={{width: '20px', height: '20px', cursor: 'pointer'}}
              />
              <label
                className='form-check-label fs-6 text-gray-900'
                htmlFor='addNote'
                style={{cursor: 'pointer', marginLeft: '12px'}}
              >
                {mode === 'Add' ? 'Add a' : 'Edit'} note
              </label>
            </div>
            <div className='form-check d-flex align-items-center'>
              <input
                className='form-check-input'
                type='radio'
                name='evidenceType'
                id='uploadEvidence'
                checked={evidenceType === 'upload'}
                onChange={() => {
                  setEvidenceType('upload')
                  setNote('')
                }}
                style={{width: '20px', height: '20px', cursor: 'pointer'}}
              />
              <label
                className='form-check-label fs-6 text-gray-900'
                htmlFor='uploadEvidence'
                style={{cursor: 'pointer', marginLeft: '12px'}}
              >
                Upload Evidence
              </label>
            </div>
          </div>
        )}

        {evidenceType === 'note' ? (
          <textarea
            className='form-control'
            rows={5}
            placeholder='Enter remediation notes...'
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{resize: 'none'}}
          />
        ) : (
          <div>
            {existingFile ? (
              <div className='mt-3 d-flex align-items-center'>
                <input
                  type='text'
                  className='form-control'
                  value={existingFile.name}
                  readOnly
                  style={{flex: 1, marginRight: '10px', height: '35px'}}
                />
                <button
                  className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-3'
                  onClick={handleDownloadFile}
                  disabled={currentUser?.role === 'Auditor'}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='oklch(44.6% 0.03 256.802)'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-download'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2' />
                    <path d='M7 11l5 5l5 -5' />
                    <path d='M12 4l0 12' />
                  </svg>
                </button>
                <button
                  className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm '
                  onClick={handleDeleteFile}
                  disabled={currentUser?.role === 'Auditor'}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='oklch(50.5% 0.213 27.518)'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-trash'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M4 7l16 0' />
                    <path d='M10 11l0 6' />
                    <path d='M14 11l0 6' />
                    <path d='M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12' />
                    <path d='M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3' />
                  </svg>
                </button>
              </div>
            ) : (
              <Dropzone
                onDrop={handleUpload}
                minSize={0}
                accept={{
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
                    '.docx',
                  ],
                  'image/jpeg': ['.jpg'],
                  'application/pdf': ['.pdf'],
                  'image/png': ['.png'],
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                  'text/csv': ['.csv'],
                }}
                maxSize={10 * 1024 * 1024}
              >
                {({getRootProps, getInputProps}) => (
                  <div
                    {...getRootProps({
                      className: 'dropzone',
                      style: {
                        backgroundColor: '#fff',
                        padding: '20px',
                        border: '2px dashed #ccc',
                        borderRadius: '4px',
                        textAlign: 'center',
                        cursor: 'pointer',
                      },
                    })}
                  >
                    <input {...getInputProps()} />
                    <img
                      src='/media/icon/uploadFile.svg'
                      alt=''
                      loading='lazy'
                      className='mb-4 mt-4'
                    />
                    <p className='fw-bold fs-6'>
                      Drag and drop, or <span style={{color: '#0e9f6e'}}>select</span> a file
                    </p>
                    <p className='fs-6'>
                      Upload files up to 10 MB of the following types: .docx, .jpg, .pdf, .png,
                      .xlsx, .csv
                    </p>
                  </div>
                )}
              </Dropzone>
            )}
            {uploadedFile && !existingFile && (
              <div className='mt-5 d-flex align-items-center'>
                <input
                  type='text'
                  className='form-control'
                  value={uploadedFile.name}
                  readOnly
                  style={{flex: 1, marginRight: '10px', height: '35px'}}
                />
                <button
                  className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm '
                  onClick={handleRemoveFile}
                  disabled={currentUser?.role === 'Auditor'}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='oklch(50.5% 0.213 27.518)'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-trash'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M4 7l16 0' />
                    <path d='M10 11l0 6' />
                    <path d='M14 11l0 6' />
                    <path d='M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12' />
                    <path d='M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3' />
                  </svg>
                </button>
              </div>
            )}
            {uploadProgress > 0 && (
              <div className='mt-3'>
                <div className='progress'>
                  <div
                    className='progress-bar'
                    role='progressbar'
                    style={{width: `${uploadProgress}%`, backgroundColor: '#0E9F6E'}}
                    aria-valuenow={uploadProgress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    {uploadProgress}%
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className='d-flex justify-content-between align-items-center p-0 px-6 py-4'>
        {(evidenceType === 'note' && mode === 'Edit') && (
          <button
            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
            onClick={handleRemoveNote}
            disabled={currentUser?.role === 'Auditor'}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 24 24'
              fill='none'
              stroke='oklch(50.5% 0.213 27.518)'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              className='icon icon-tabler icons-tabler-outline icon-tabler-trash'
            >
              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
              <path d='M4 7l16 0' />
              <path d='M10 11l0 6' />
              <path d='M14 11l0 6' />
              <path d='M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12' />
              <path d='M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3' />
            </svg>
          </button>
        )}
        <div className='d-flex gap-2 ms-auto'>
          <Button onClick={handleClose}>Cancel</Button>
          <Button btnType='primary' onClick={handleSubmit}>
            {isLoading || isNoteSaving ? (
              <>
                Submitting...{' '}
                <span className='spinner-border spinner-border-sm align-middle'></span>
              </>
            ) : (
              'Submit'
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default UploadEvidenceModal
